import { inject, Injectable } from "@angular/core";
import { StreamingHubService } from "./streaming-hub.service";
import { fromEvent, Observable } from "rxjs";
import { guid, Student } from "@pcp/core/interfaces";

/**
 * Payload for NotifyVideoUploadCompleted SignalR message.
 * Sent when video processing completes successfully and thumbnail is available.
 */
export interface VideoUploadCompletedMessage {
    slideId: number;
    vimeoId: number;
    customer: string;
    teacher: string;
    processingDuration: string; // C# TimeSpan format: "HH:MM:SS.FFFFFFF" (e.g., "00:00:31.8872540")
}

/**
 * Payload for NotifyVideoUploadFailed SignalR message.
 * Sent when video upload or processing fails.
 */
export interface VideoUploadFailedMessage {
    slideId: number;
    errorMessage: string;
    failureStage: 'Upload' | 'ThumbnailProcessing' | 'VideoProcessing';
    retryCount: number;
}

@Injectable({
    providedIn: 'root'
})
export class NotificationHubFacade {

    private readonly streamingHub = inject(StreamingHubService);
    private readonly connection = this.streamingHub.connection;

    readonly onStudentRegistered$: Observable<Student> = fromEvent(this.connection, 'NotifyStudentRegistered');
    readonly onNotifyReloadCustomerBookmarks$: Observable<Student> = fromEvent(this.connection, 'NotifyReloadCustomerBookmarks');
    readonly onNotifyReloadCustomerFavorites$: Observable<Student> = fromEvent(this.connection, 'NotifyReloadCustomerFavorites');

    readonly OnCustomerModuleExported$: Observable<any> = fromEvent(this.connection, 'NotifyCustomerModuleExported');
    readonly OnCustomerModuleImported$: Observable<any> = fromEvent(this.connection, 'NotifyCustomerModuleImported');
    readonly OnCustomerModuleError$: Observable<any> = fromEvent(this.connection, 'NotifyCustomerModuleError');

    /**
     * Received notification from teacher that a lesson has been stopped.
     * Used to stop the local lesson when the same user has stopped the lesson from another browser.
     */
    readonly onNotifyLessonHasBeenStopped$: Observable<guid> = fromEvent(this.connection, 'NotifyLessonHasBeenStopped');

    /**
     * Received notification when video upload and processing completes successfully.
     * Used to replace placeholder thumbnail with actual thumbnail in edit-workspace.
     */
    readonly onVideoUploadCompleted$: Observable<VideoUploadCompletedMessage> = fromEvent(this.connection, 'NotifyVideoUploadCompleted');

    /**
     * Received notification when video upload or processing fails.
     * Used for error logging and maintaining placeholder display.
     */
    readonly onVideoUploadFailed$: Observable<VideoUploadFailedMessage> = fromEvent(this.connection, 'NotifyVideoUploadFailed');

}
